import * as React from "react";
import { cn } from "@/lib/utils";
import { focusRing, disabled as disabledStyles } from "@/lib/theme";

export interface SwitchProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onChange" | "type"
> {
  /**
   * Whether the switch is checked
   */
  checked?: boolean;
  /**
   * Callback when the checked state changes
   */
  onCheckedChange?: (checked: boolean) => void;
  /**
   * Size variant of the switch
   * @default "default"
   */
  size?: "sm" | "default" | "lg";
  /**
   * Whether to show a label inline with the switch
   */
  label?: React.ReactNode;
  /**
   * Position of the label
   * @default "right"
   */
  labelPosition?: "left" | "right";
}

/**
 * Switch component with consistent styling from the design system.
 * Supports different sizes and label positioning.
 *
 * @example
 * <Switch checked={true} onCheckedChange={setChecked} />
 * <Switch size="sm" label="Enable notifications" />
 * <Switch size="lg" label="Dark mode" labelPosition="left" />
 */
export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked = false,
      onCheckedChange,
      disabled = false,
      size = "default",
      label,
      labelPosition = "right",
      className,
      id,
      ...props
    },
    ref,
  ) => {
    // Size configurations
    const sizes = {
      sm: {
        switch: "h-5 w-9",
        thumb: "h-4 w-4",
        translate: "translate-x-4",
        label: "text-sm",
      },
      default: {
        switch: "h-6 w-11",
        thumb: "h-5 w-5",
        translate: "translate-x-5",
        label: "text-sm",
      },
      lg: {
        switch: "h-7 w-14",
        thumb: "h-6 w-6",
        translate: "translate-x-7",
        label: "text-base",
      },
    };

    const sizeConfig = sizes[size];

    const switchElement = (
      <button
        ref={ref}
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onCheckedChange?.(!checked)}
        data-slot="control"
        className={cn(
          // Base styles
          "relative inline-flex shrink-0 cursor-pointer items-center",
          "rounded-full border-2 border-transparent",
          "transition-colors duration-200 ease-in-out",
          // Size
          sizeConfig.switch,
          // Colors based on state
          checked ? "bg-primary" : "bg-input",
          // Focus styles
          !disabled && focusRing(),
          // Disabled styles
          disabled && disabledStyles(),
          disabled && "cursor-not-allowed",
          // Custom className (without label)
          !label && className,
        )}
        {...props}
      >
        <span
          aria-hidden="true"
          className={cn(
            // Base styles
            "pointer-events-none inline-block rounded-full",
            "bg-background shadow-lg ring-0",
            "transition duration-200 ease-in-out",
            // Size
            sizeConfig.thumb,
            // Translation based on checked state
            checked ? sizeConfig.translate : "translate-x-0",
          )}
        />
      </button>
    );

    // If no label, return switch only
    if (!label) {
      return switchElement;
    }

    // With label
    return (
      <label
        className={cn(
          "inline-flex items-center gap-2 cursor-pointer",
          disabled && "cursor-not-allowed",
          className,
        )}
      >
        {labelPosition === "left" && (
          <span className={cn(sizeConfig.label, "select-none", disabled && "opacity-50")}>
            {label}
          </span>
        )}
        {switchElement}
        {labelPosition === "right" && (
          <span className={cn(sizeConfig.label, "select-none", disabled && "opacity-50")}>
            {label}
          </span>
        )}
      </label>
    );
  },
);

Switch.displayName = "Switch";
